/**
 * Usage counting — "which features are used, and which are never touched".
 *
 * The whole contract in one place:
 *
 *   • It counts FEATURES: keys, the app version and the OS. No identifier
 *     rides along (transport.js; the install licence that used to was removed
 *     on 26.10.2026). Do not add one here.
 *   • Only keys from `features.js` can be sent. A caller-supplied string is
 *     dropped, so no URL, body, header or collection name can leak.
 *   • Each key is reported at most once per UTC day per install (`dailyMark.js`).
 *   • It is OFF until the user says yes (owner, 16.10.2026): opt-in, asked
 *     once after sign-in (UsageConsentCard), default
 *     off, and off again the moment `sendUsageData` is switched off in
 *     Settings › Privacy. `bootstrap.js` decides; this module is closed
 *     until it is wired.
 *   • Nothing here may throw into a caller. Recording usage is never worth
 *     breaking the thing being used.
 *
 * Usage:
 *   import { recordFeature, PANEL } from '../services/telemetry';
 *   recordFeature(PANEL.DB_MANAGER);
 */
import { isFeatureKey } from './features';
import { claimDaily, clearDailyMarks, releaseDaily } from './dailyMark';
import { sendUsage } from './transport';

export { PANEL, REQUEST, ACTION, MCP, TERMINAL, VAULT, terminalShellKeyFor, terminalAgentKeyFor } from './features';

/** Wait this long after the first key before flushing, so a burst at startup rides one request. */
const BATCH_MS = 5000;

const pending = new Set();
let timer = null;
// Replaced by init(). Default CLOSED (16.10.2026): usage is shared only after
// the user said yes, so a key recorded before the app wired consent is dropped.
let readEnabled = () => false;
let readEnv = () => ({});

/**
 * Wire the module to the app's preference store and environment.
 * Called once from the app shell; safe to call again (the last call wins).
 *
 * @param {{ isEnabled: () => boolean, getEnv: () => ({appVersion?: string, os?: string}) }} deps
 */
export function initTelemetry({ isEnabled, getEnv } = {}) {
  if (typeof isEnabled === 'function') readEnabled = isEnabled;
  if (typeof getEnv === 'function') readEnv = getEnv;
}

function flush() {
  timer = null;
  if (pending.size === 0) return;
  const keys = [...pending];
  pending.clear();
  // Re-check consent at send time: the user may have switched it off while the batch waited.
  if (!enabled()) return;
  let env = {};
  try { env = readEnv() || {}; } catch { env = {}; }
  // A failed send gives the day's marks back, so the next use of those features
  // reports again instead of going quiet until tomorrow. The batch itself is
  // still dropped rather than spooled — see transport.js for why.
  sendUsage(keys, env).then((ok) => { if (!ok) releaseDaily(keys); });
}

function enabled() {
  try { return readEnabled() !== false; } catch { return false; }
}

/**
 * Note that a feature was used. Cheap, synchronous, and safe to call from a
 * render path or an event handler — the network happens later, batched.
 *
 * @param {string} key one of the constants in `features.js`
 */
export function recordFeature(key) {
  try {
    if (!enabled()) return;
    if (!isFeatureKey(key)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[telemetry] "${key}" is not in the feature allow-list — not sent`);
      }
      return;
    }
    if (!claimDaily(key)) return;            // already counted today
    pending.add(key);
    if (timer === null) timer = setTimeout(flush, BATCH_MS);
  } catch {
    // counting must never break the feature being counted
  }
}

/**
 * Called when the user switches usage counting off: drop anything queued and
 * forget today's marks, so switching it back on starts reporting again instead
 * of staying silent until tomorrow.
 */
export function resetTelemetry() {
  pending.clear();
  if (timer !== null) { clearTimeout(timer); timer = null; }
  clearDailyMarks();
}

/** Send whatever is queued right now — used when the window is closing. */
export function flushTelemetryNow() {
  if (timer !== null) { clearTimeout(timer); timer = null; }
  flush();
}
